// Texts of the workbench area (GIOTHUB-185). English defines the keys.
import type { Dict } from "./dict";

const en = {
  // title bar
  "wb.breadcrumb": "Breadcrumb",
  "wb.draft": "draft r{rev}",
  "wb.search.aria": "Search keys",
  "wb.search.placeholder": "Search keys, labels, roles",
  "wb.search.kbd": "Ctrl K",
  "wb.val.none": "Not validated",
  "wb.val.ok.one": "Valid · {count} warning",
  "wb.val.ok.many": "Valid · {count} warnings",
  "wb.val.err.one": "{count} error",
  "wb.val.err.many": "{count} errors",
  "wb.keys.count": "{done} / {total} keys",
  "wb.theme": "Switch light or dark",
  "wb.assistant.toggle": "Show or hide the assistant",
  "wb.publish": "Publish",
  "wb.publish.blocked": "{count} review item(s) block publishing",
  "wb.publish.title": "Versions and publishing",
  // activity bar
  "wb.act.nav": "Workbench",
  "wb.act.explorer": "Explorer",
  "wb.act.design": "Design: requirements and protocol",
  "wb.act.review": "Review",
  "wb.act.validate": "Validate the draft",
  "wb.act.examples": "Examples and samples",
  "wb.act.help": "Help - user manual",
  // explorer
  "wb.exp.package": "Package",
  "wb.exp.add.c": "Add package files (decode.js, encode.js, samples)",
  "wb.exp.add.pb": "Add package files (.proto, samples)",
  "wb.exp.empty.c": "Nothing yet: press Create the package in the set-up to start.",
  "wb.exp.empty.b": "Nothing yet: create the package, then describe the device in the Design tab.",
  "wb.exp.empty.a": "No sources yet: upload the .proto to start.",
  "wb.exp.showfiles": "Show the files",
  "wb.exp.fold": "Fold",
  "wb.exp.ai": "AI",
  "wb.exp.inputs": "Inputs",
  "wb.exp.add.inputs": "Add documents (PDF, images, captures)",
  "wb.exp.inputs.empty": "Datasheets, protocol documents and captures the AI may read.",
  "wb.exp.groups": "Key groups",
  "wb.exp.messages": "Messages",
  "wb.group.commands": "Commands",
  "wb.group.keys": "Keys",
  // tabs
  "wb.tab.design": "Design",
  "wb.tab.review": "Review · {group}",
  "wb.tab.examples": "Examples",
  "wb.tab.close": "Close {file}",
  // review toolbar
  "wb.filter.aria": "Filter",
  "wb.filter.all": "All {count}",
  "wb.filter.review": "To review {count}",
  "wb.filter.low": "Low confidence {count}",
  "wb.filter.questions": "Questions {count}",
  "wb.selected": "{count} selected",
  "wb.reject": "Reject",
  "wb.accept.n": "Accept {count}",
  "wb.adopt.title": "The assistant changed its proposal for keys you had accepted: take every new proposal.",
  "wb.adopt.title.edited": "{count} key(s) you edited are left out - open them to compare and take the new proposal one by one.",
  "wb.adopt.busy": "Taking…",
  "wb.adopt": "Take all new proposals ({count})",
  "wb.accepthigh": "Accept all high confidence ({count})",
  "wb.written": "Written to the package",
  "wb.write.title.c": "Write the accepted and edited keys into catalogue.json and validate",
  "wb.write.title.pb": "Write the accepted and edited keys into classification.yaml, regenerate catalogue.json and validate",
  "wb.write.busy": "Writing…",
  "wb.write": "Write {count} to package",
  // review grid
  "wb.grid.search": "Search \"{q}\"",
  "wb.col.key": "Key",
  "wb.col.class": "Class",
  "wb.col.kind": "Kind · unit",
  "wb.col.role": "Role · qualifiers",
  "wb.col.label": "Label",
  "wb.col.why": "Why",
  "wb.col.conf": "Confidence",
  "wb.col.state": "State",
  "wb.grid.shown.one": "{count} key shown",
  "wb.grid.shown.many": "{count} keys shown",
  "wb.grid.none": "Nothing to show with this filter.",
  "wb.grid.select": "Select {key}",
  "wb.grid.diff.title": "The AI proposes a change",
  "wb.state.accepted": "accepted",
  "wb.state.proposed": "proposed",
  "wb.state.edited": "edited",
  "wb.state.question": "question",
  "wb.state.rejected": "rejected",
  "wb.state.open": "open",
  "wb.state.answered": "answered",
  "wb.state.dismissed": "dismissed",
  "wb.conf.high": "high",
  "wb.conf.medium": "medium",
  "wb.conf.low": "low",
  // revisions and samples
  "wb.rev.none": "No revision yet.",
  "wb.rev.ai": "AI",
  "wb.rev.person": "person",
  "wb.geo.positions": "{count} position(s)",
  "wb.geo.wifi": "{count} Wi-Fi",
  "wb.geo.cells": "{count} cell(s)",
  "wb.geo.ble": "{count} BLE",
  "wb.geo.note": " · geo: {list}",
  "wb.smp.nopkg": "No package yet.",
  "wb.smp.running": "Running the samples…",
  "wb.smp.summary": "{ok} of {total} samples decode as expected (the store asks for at least 5).",
  "wb.smp.open": "Open the Examples tab",
  "wb.smp.ok": "decodes as expected · {ms} ms{geo}",
  "wb.smp.differs": "differs",
  // bottom panel
  "wb.panel.aria": "Panel",
  "wb.panel.problems": "Problems",
  "wb.panel.samples": "Samples",
  "wb.panel.revisions": "Revisions",
  "wb.panel.engine": "glmdt check · engine {engine} · {ms} ms · r{rev}",
  "wb.panel.checking": "Checking…",
  "wb.panel.validate": "Validate",
  "wb.panel.notvalidated": "Not validated yet.",
  // assistant
  "wb.ai.aria": "AI assistant",
  "wb.ai.title": "Assistant",
  "wb.ai.scenario": "Scenario {s} · {kind}",
  "wb.ai.paused": "Paused",
  "wb.ai.working": "Working",
  "wb.ai.ready": "Ready",
  "wb.ai.hide": "Hide the assistant",
  "wb.ai.pausedtext": "The AI is paused: the console's gateway is not reachable. The project stays editable; the conversation continues when it is back.",
  "wb.ai.empty.nofiles": "Create the package first (set-up, step 1): the assistant needs the manifest to check its work. Then upload the documents and ask it to start.",
  "wb.ai.empty.c": "Ask the assistant to write the decoder. It reads your documents and examples, writes decode.js, proposes every key it emits in the review grid and runs your examples until they pass; it asks where it is not sure. Nothing reaches the package until you accept it.",
  "wb.ai.empty.b": "Describe the device in the Design tab, then ask the assistant to draft the requirements. After you confirm them it designs the protocol and classifies it. Nothing reaches the package until you accept it.",
  "wb.ai.empty.a": "Ask the assistant to classify the .proto. It reads the schema and your documents, proposes every key in the review grid and asks where it is not sure. Nothing reaches the package until you accept it.",
  "wb.ai.thinking": "Thinking…",
  "wb.ai.question": "Question {n} of {total}",
  "wb.ai.question.blocking": "Question {n} of {total} · blocks publishing",
  "wb.ai.answer.label": "Your answer",
  "wb.ai.answer": "Answer",
  "wb.ai.dismiss": "Dismiss",
  "wb.ai.ask": "Ask the assistant",
  "wb.ai.ph.questions": "Answered the questions? Tell the assistant to continue",
  "wb.ai.ph.c": "e.g. Write the decoder from the uploaded documents and my examples",
  "wb.ai.ph.a": "Ask for a change, e.g. use role signal_strength for every *_rssi_dbm key",
  "wb.ai.busy": "The assistant is working…",
  "wb.ai.hint": "Enter to send · Shift+Enter new line",
  "wb.ai.send": "Send",
  // status bar
  "wb.st.draft": "draft · r{rev}",
  "wb.st.engine": "Engine {engine}",
  "wb.st.quickjs": "QuickJS sandbox",
  "wb.st.pb": "protobuf decoder",
  "wb.st.demo": "Demo data",
  "wb.st.region": "Region {region}",
  "wb.st.usage": "AI usage today {usage}",
  // file view
  "wb.file.loading": "Loading {path}…",
  "wb.file.binary": "{path} is a binary file (a sample payload): it is decoded in the Samples panel.",
  "wb.file.generated": "generated by the store engine",
  "wb.file.readonly": "read-only",
  "wb.file.unsaved": "unsaved changes",
  "wb.file.discard": "Discard",
  "wb.file.saving": "Saving…",
  "wb.file.save": "Save as revision",
  "wb.file.content": "Content of {path}",
} as const;

const de = {
  "wb.breadcrumb": "Navigationspfad",
  "wb.draft": "Entwurf r{rev}",
  "wb.search.aria": "Schlüssel suchen",
  "wb.search.placeholder": "Schlüssel, Bezeichnungen, Rollen suchen",
  "wb.search.kbd": "Strg K",
  "wb.val.none": "Nicht geprüft",
  "wb.val.ok.one": "Gültig · {count} Warnung",
  "wb.val.ok.many": "Gültig · {count} Warnungen",
  "wb.val.err.one": "{count} Fehler",
  "wb.val.err.many": "{count} Fehler",
  "wb.keys.count": "{done} / {total} Schlüssel",
  "wb.theme": "Hell oder dunkel umschalten",
  "wb.assistant.toggle": "Assistent ein- oder ausblenden",
  "wb.publish": "Veröffentlichen",
  "wb.publish.blocked": "{count} Prüfpunkt(e) blockieren die Veröffentlichung",
  "wb.publish.title": "Versionen und Veröffentlichung",
  "wb.act.nav": "Arbeitsbereich",
  "wb.act.explorer": "Explorer",
  "wb.act.design": "Design: Anforderungen und Protokoll",
  "wb.act.review": "Prüfung",
  "wb.act.validate": "Entwurf prüfen",
  "wb.act.examples": "Beispiele",
  "wb.act.help": "Hilfe - Benutzerhandbuch",
  "wb.exp.package": "Paket",
  "wb.exp.add.c": "Paketdateien hinzufügen (decode.js, encode.js, Beispiele)",
  "wb.exp.add.pb": "Paketdateien hinzufügen (.proto, Beispiele)",
  "wb.exp.empty.c": "Noch nichts: Drücken Sie in der Einrichtung auf «Paket erstellen», um zu beginnen.",
  "wb.exp.empty.b": "Noch nichts: Erstellen Sie das Paket und beschreiben Sie dann das Gerät im Tab Design.",
  "wb.exp.empty.a": "Noch keine Quellen: Laden Sie die .proto hoch, um zu beginnen.",
  "wb.exp.showfiles": "Dateien anzeigen",
  "wb.exp.fold": "Einklappen",
  "wb.exp.ai": "KI",
  "wb.exp.inputs": "Unterlagen",
  "wb.exp.add.inputs": "Dokumente hinzufügen (PDF, Bilder, Mitschnitte)",
  "wb.exp.inputs.empty": "Datenblätter, Protokolldokumente und Mitschnitte, die die KI lesen darf.",
  "wb.exp.groups": "Schlüsselgruppen",
  "wb.exp.messages": "Nachrichten",
  "wb.group.commands": "Befehle",
  "wb.group.keys": "Schlüssel",
  "wb.tab.design": "Design",
  "wb.tab.review": "Prüfung · {group}",
  "wb.tab.examples": "Beispiele",
  "wb.tab.close": "{file} schliessen",
  "wb.filter.aria": "Filter",
  "wb.filter.all": "Alle {count}",
  "wb.filter.review": "Zu prüfen {count}",
  "wb.filter.low": "Geringe Sicherheit {count}",
  "wb.filter.questions": "Fragen {count}",
  "wb.selected": "{count} ausgewählt",
  "wb.reject": "Ablehnen",
  "wb.accept.n": "{count} annehmen",
  "wb.adopt.title": "Der Assistent hat seinen Vorschlag für Schlüssel geändert, die Sie angenommen hatten: alle neuen Vorschläge übernehmen.",
  "wb.adopt.title.edited": "{count} von Ihnen bearbeitete(r) Schlüssel bleiben aussen vor - öffnen Sie sie, um zu vergleichen und den neuen Vorschlag einzeln zu übernehmen.",
  "wb.adopt.busy": "Wird übernommen…",
  "wb.adopt": "Alle neuen Vorschläge übernehmen ({count})",
  "wb.accepthigh": "Alle mit hoher Sicherheit annehmen ({count})",
  "wb.written": "Ins Paket geschrieben",
  "wb.write.title.c": "Die angenommenen und bearbeiteten Schlüssel in catalogue.json schreiben und prüfen",
  "wb.write.title.pb": "Die angenommenen und bearbeiteten Schlüssel in classification.yaml schreiben, catalogue.json neu erzeugen und prüfen",
  "wb.write.busy": "Wird geschrieben…",
  "wb.write": "{count} ins Paket schreiben",
  "wb.grid.search": "Suche «{q}»",
  "wb.col.key": "Schlüssel",
  "wb.col.class": "Klasse",
  "wb.col.kind": "Art · Einheit",
  "wb.col.role": "Rolle · Qualifikatoren",
  "wb.col.label": "Bezeichnung",
  "wb.col.why": "Begründung",
  "wb.col.conf": "Sicherheit",
  "wb.col.state": "Status",
  "wb.grid.shown.one": "{count} Schlüssel angezeigt",
  "wb.grid.shown.many": "{count} Schlüssel angezeigt",
  "wb.grid.none": "Mit diesem Filter gibt es nichts anzuzeigen.",
  "wb.grid.select": "{key} auswählen",
  "wb.grid.diff.title": "Die KI schlägt eine Änderung vor",
  "wb.state.accepted": "angenommen",
  "wb.state.proposed": "vorgeschlagen",
  "wb.state.edited": "bearbeitet",
  "wb.state.question": "Frage",
  "wb.state.rejected": "abgelehnt",
  "wb.state.open": "offen",
  "wb.state.answered": "beantwortet",
  "wb.state.dismissed": "verworfen",
  "wb.conf.high": "hoch",
  "wb.conf.medium": "mittel",
  "wb.conf.low": "niedrig",
  "wb.rev.none": "Noch keine Revision.",
  "wb.rev.ai": "KI",
  "wb.rev.person": "Person",
  "wb.geo.positions": "{count} Position(en)",
  "wb.geo.wifi": "{count} WLAN",
  "wb.geo.cells": "{count} Zelle(n)",
  "wb.geo.ble": "{count} BLE",
  "wb.geo.note": " · Geo: {list}",
  "wb.smp.nopkg": "Noch kein Paket.",
  "wb.smp.running": "Beispiele werden ausgeführt…",
  "wb.smp.summary": "{ok} von {total} Beispielen werden wie erwartet dekodiert (der Store verlangt mindestens 5).",
  "wb.smp.open": "Tab Beispiele öffnen",
  "wb.smp.ok": "wie erwartet dekodiert · {ms} ms{geo}",
  "wb.smp.differs": "weicht ab",
  "wb.panel.aria": "Bereich",
  "wb.panel.problems": "Probleme",
  "wb.panel.samples": "Beispiele",
  "wb.panel.revisions": "Revisionen",
  "wb.panel.engine": "glmdt check · Engine {engine} · {ms} ms · r{rev}",
  "wb.panel.checking": "Wird geprüft…",
  "wb.panel.validate": "Prüfen",
  "wb.panel.notvalidated": "Noch nicht geprüft.",
  "wb.ai.aria": "KI-Assistent",
  "wb.ai.title": "Assistent",
  "wb.ai.scenario": "Szenario {s} · {kind}",
  "wb.ai.paused": "Pausiert",
  "wb.ai.working": "Arbeitet",
  "wb.ai.ready": "Bereit",
  "wb.ai.hide": "Assistent ausblenden",
  "wb.ai.pausedtext": "Die KI ist pausiert: Das Gateway der Konsole ist nicht erreichbar. Das Projekt bleibt bearbeitbar; das Gespräch geht weiter, sobald es wieder da ist.",
  "wb.ai.empty.nofiles": "Erstellen Sie zuerst das Paket (Einrichtung, Schritt 1): Der Assistent braucht das Manifest, um seine Arbeit zu prüfen. Laden Sie dann die Dokumente hoch und bitten Sie ihn zu beginnen.",
  "wb.ai.empty.c": "Bitten Sie den Assistenten, den Decoder zu schreiben. Er liest Ihre Dokumente und Beispiele, schreibt decode.js, schlägt jeden ausgegebenen Schlüssel in der Prüfliste vor und führt Ihre Beispiele aus, bis sie bestehen; wo er unsicher ist, fragt er nach. Nichts gelangt ins Paket, bevor Sie es annehmen.",
  "wb.ai.empty.b": "Beschreiben Sie das Gerät im Tab Design und bitten Sie dann den Assistenten, die Anforderungen zu entwerfen. Nachdem Sie sie bestätigt haben, entwirft er das Protokoll und klassifiziert es. Nichts gelangt ins Paket, bevor Sie es annehmen.",
  "wb.ai.empty.a": "Bitten Sie den Assistenten, die .proto zu klassifizieren. Er liest das Schema und Ihre Dokumente, schlägt jeden Schlüssel in der Prüfliste vor und fragt nach, wo er unsicher ist. Nichts gelangt ins Paket, bevor Sie es annehmen.",
  "wb.ai.thinking": "Denkt nach…",
  "wb.ai.question": "Frage {n} von {total}",
  "wb.ai.question.blocking": "Frage {n} von {total} · blockiert die Veröffentlichung",
  "wb.ai.answer.label": "Ihre Antwort",
  "wb.ai.answer": "Antworten",
  "wb.ai.dismiss": "Verwerfen",
  "wb.ai.ask": "Assistenten fragen",
  "wb.ai.ph.questions": "Fragen beantwortet? Sagen Sie dem Assistenten, er soll weitermachen",
  "wb.ai.ph.c": "z. B. Schreibe den Decoder aus den hochgeladenen Dokumenten und meinen Beispielen",
  "wb.ai.ph.a": "Bitten Sie um eine Änderung, z. B. Rolle signal_strength für jeden *_rssi_dbm-Schlüssel",
  "wb.ai.busy": "Der Assistent arbeitet…",
  "wb.ai.hint": "Eingabe zum Senden · Umschalt+Eingabe für neue Zeile",
  "wb.ai.send": "Senden",
  "wb.st.draft": "Entwurf · r{rev}",
  "wb.st.engine": "Engine {engine}",
  "wb.st.quickjs": "QuickJS-Sandbox",
  "wb.st.pb": "Protobuf-Decoder",
  "wb.st.demo": "Demodaten",
  "wb.st.region": "Region {region}",
  "wb.st.usage": "KI-Nutzung heute {usage}",
  "wb.file.loading": "{path} wird geladen…",
  "wb.file.binary": "{path} ist eine Binärdatei (eine Beispiel-Nutzlast): Sie wird im Bereich Beispiele dekodiert.",
  "wb.file.generated": "von der Store-Engine erzeugt",
  "wb.file.readonly": "schreibgeschützt",
  "wb.file.unsaved": "ungespeicherte Änderungen",
  "wb.file.discard": "Verwerfen",
  "wb.file.saving": "Wird gespeichert…",
  "wb.file.save": "Als Revision speichern",
  "wb.file.content": "Inhalt von {path}",
} satisfies Dict<typeof en>;

const it = {
  "wb.breadcrumb": "Percorso",
  "wb.draft": "bozza r{rev}",
  "wb.search.aria": "Cerca chiavi",
  "wb.search.placeholder": "Cerca chiavi, etichette, ruoli",
  "wb.search.kbd": "Ctrl K",
  "wb.val.none": "Non verificato",
  "wb.val.ok.one": "Valido · {count} avviso",
  "wb.val.ok.many": "Valido · {count} avvisi",
  "wb.val.err.one": "{count} errore",
  "wb.val.err.many": "{count} errori",
  "wb.keys.count": "{done} / {total} chiavi",
  "wb.theme": "Passa a chiaro o scuro",
  "wb.assistant.toggle": "Mostra o nascondi l'assistente",
  "wb.publish": "Pubblica",
  "wb.publish.blocked": "{count} elemento/i della revisione bloccano la pubblicazione",
  "wb.publish.title": "Versioni e pubblicazione",
  "wb.act.nav": "Area di lavoro",
  "wb.act.explorer": "Esplora",
  "wb.act.design": "Design: requisiti e protocollo",
  "wb.act.review": "Revisione",
  "wb.act.validate": "Verifica la bozza",
  "wb.act.examples": "Esempi",
  "wb.act.help": "Aiuto - manuale utente",
  "wb.exp.package": "Pacchetto",
  "wb.exp.add.c": "Aggiungi file del pacchetto (decode.js, encode.js, esempi)",
  "wb.exp.add.pb": "Aggiungi file del pacchetto (.proto, esempi)",
  "wb.exp.empty.c": "Ancora niente: premere «Crea il pacchetto» nella configurazione per iniziare.",
  "wb.exp.empty.b": "Ancora niente: creare il pacchetto, poi descrivere il dispositivo nella scheda Design.",
  "wb.exp.empty.a": "Nessuna sorgente: caricare il .proto per iniziare.",
  "wb.exp.showfiles": "Mostra i file",
  "wb.exp.fold": "Comprimi",
  "wb.exp.ai": "IA",
  "wb.exp.inputs": "Documenti",
  "wb.exp.add.inputs": "Aggiungi documenti (PDF, immagini, acquisizioni)",
  "wb.exp.inputs.empty": "Schede tecniche, documenti di protocollo e acquisizioni che l'IA può leggere.",
  "wb.exp.groups": "Gruppi di chiavi",
  "wb.exp.messages": "Messaggi",
  "wb.group.commands": "Comandi",
  "wb.group.keys": "Chiavi",
  "wb.tab.design": "Design",
  "wb.tab.review": "Revisione · {group}",
  "wb.tab.examples": "Esempi",
  "wb.tab.close": "Chiudi {file}",
  "wb.filter.aria": "Filtro",
  "wb.filter.all": "Tutte {count}",
  "wb.filter.review": "Da rivedere {count}",
  "wb.filter.low": "Affidabilità bassa {count}",
  "wb.filter.questions": "Domande {count}",
  "wb.selected": "{count} selezionate",
  "wb.reject": "Rifiuta",
  "wb.accept.n": "Accetta {count}",
  "wb.adopt.title": "L'assistente ha cambiato la proposta per chiavi già accettate: adotta tutte le nuove proposte.",
  "wb.adopt.title.edited": "{count} chiave/i modificate da Lei restano escluse: aprirle per confrontare e adottare la nuova proposta una per una.",
  "wb.adopt.busy": "Adozione…",
  "wb.adopt": "Adotta tutte le nuove proposte ({count})",
  "wb.accepthigh": "Accetta tutte con affidabilità alta ({count})",
  "wb.written": "Scritto nel pacchetto",
  "wb.write.title.c": "Scrive le chiavi accettate e modificate in catalogue.json e verifica",
  "wb.write.title.pb": "Scrive le chiavi accettate e modificate in classification.yaml, rigenera catalogue.json e verifica",
  "wb.write.busy": "Scrittura…",
  "wb.write": "Scrivi {count} nel pacchetto",
  "wb.grid.search": "Ricerca «{q}»",
  "wb.col.key": "Chiave",
  "wb.col.class": "Classe",
  "wb.col.kind": "Tipo · unità",
  "wb.col.role": "Ruolo · qualificatori",
  "wb.col.label": "Etichetta",
  "wb.col.why": "Motivo",
  "wb.col.conf": "Affidabilità",
  "wb.col.state": "Stato",
  "wb.grid.shown.one": "{count} chiave mostrata",
  "wb.grid.shown.many": "{count} chiavi mostrate",
  "wb.grid.none": "Niente da mostrare con questo filtro.",
  "wb.grid.select": "Seleziona {key}",
  "wb.grid.diff.title": "L'IA propone una modifica",
  "wb.state.accepted": "accettata",
  "wb.state.proposed": "proposta",
  "wb.state.edited": "modificata",
  "wb.state.question": "domanda",
  "wb.state.rejected": "rifiutata",
  "wb.state.open": "aperta",
  "wb.state.answered": "con risposta",
  "wb.state.dismissed": "ignorata",
  "wb.conf.high": "alta",
  "wb.conf.medium": "media",
  "wb.conf.low": "bassa",
  "wb.rev.none": "Ancora nessuna revisione.",
  "wb.rev.ai": "IA",
  "wb.rev.person": "persona",
  "wb.geo.positions": "{count} posizione/i",
  "wb.geo.wifi": "{count} Wi-Fi",
  "wb.geo.cells": "{count} cella/e",
  "wb.geo.ble": "{count} BLE",
  "wb.geo.note": " · geo: {list}",
  "wb.smp.nopkg": "Ancora nessun pacchetto.",
  "wb.smp.running": "Esecuzione degli esempi…",
  "wb.smp.summary": "{ok} di {total} esempi decodificati come previsto (lo store ne chiede almeno 5).",
  "wb.smp.open": "Apri la scheda Esempi",
  "wb.smp.ok": "decodificato come previsto · {ms} ms{geo}",
  "wb.smp.differs": "differisce",
  "wb.panel.aria": "Pannello",
  "wb.panel.problems": "Problemi",
  "wb.panel.samples": "Esempi",
  "wb.panel.revisions": "Revisioni",
  "wb.panel.engine": "glmdt check · engine {engine} · {ms} ms · r{rev}",
  "wb.panel.checking": "Verifica…",
  "wb.panel.validate": "Verifica",
  "wb.panel.notvalidated": "Non ancora verificato.",
  "wb.ai.aria": "Assistente IA",
  "wb.ai.title": "Assistente",
  "wb.ai.scenario": "Scenario {s} · {kind}",
  "wb.ai.paused": "In pausa",
  "wb.ai.working": "Al lavoro",
  "wb.ai.ready": "Pronto",
  "wb.ai.hide": "Nascondi l'assistente",
  "wb.ai.pausedtext": "L'IA è in pausa: il gateway della console non è raggiungibile. Il progetto resta modificabile; la conversazione riprende quando torna disponibile.",
  "wb.ai.empty.nofiles": "Creare prima il pacchetto (configurazione, passo 1): l'assistente ha bisogno del manifest per verificare il suo lavoro. Poi caricare i documenti e chiedergli di iniziare.",
  "wb.ai.empty.c": "Chieda all'assistente di scrivere il decoder. Legge i Suoi documenti ed esempi, scrive decode.js, propone ogni chiave emessa nella griglia di revisione ed esegue i Suoi esempi finché passano; chiede dove non è sicuro. Nulla entra nel pacchetto finché non lo accetta.",
  "wb.ai.empty.b": "Descriva il dispositivo nella scheda Design, poi chieda all'assistente di redigere i requisiti. Dopo la Sua conferma progetta il protocollo e lo classifica. Nulla entra nel pacchetto finché non lo accetta.",
  "wb.ai.empty.a": "Chieda all'assistente di classificare il .proto. Legge lo schema e i Suoi documenti, propone ogni chiave nella griglia di revisione e chiede dove non è sicuro. Nulla entra nel pacchetto finché non lo accetta.",
  "wb.ai.thinking": "Sta pensando…",
  "wb.ai.question": "Domanda {n} di {total}",
  "wb.ai.question.blocking": "Domanda {n} di {total} · blocca la pubblicazione",
  "wb.ai.answer.label": "La Sua risposta",
  "wb.ai.answer": "Rispondi",
  "wb.ai.dismiss": "Ignora",
  "wb.ai.ask": "Chiedi all'assistente",
  "wb.ai.ph.questions": "Ha risposto alle domande? Dica all'assistente di continuare",
  "wb.ai.ph.c": "es. Scrivi il decoder dai documenti caricati e dai miei esempi",
  "wb.ai.ph.a": "Chieda una modifica, es. usa il ruolo signal_strength per ogni chiave *_rssi_dbm",
  "wb.ai.busy": "L'assistente sta lavorando…",
  "wb.ai.hint": "Invio per inviare · Maiusc+Invio nuova riga",
  "wb.ai.send": "Invia",
  "wb.st.draft": "bozza · r{rev}",
  "wb.st.engine": "Engine {engine}",
  "wb.st.quickjs": "Sandbox QuickJS",
  "wb.st.pb": "decoder protobuf",
  "wb.st.demo": "Dati demo",
  "wb.st.region": "Regione {region}",
  "wb.st.usage": "Uso IA oggi {usage}",
  "wb.file.loading": "Caricamento di {path}…",
  "wb.file.binary": "{path} è un file binario (un payload di esempio): viene decodificato nel pannello Esempi.",
  "wb.file.generated": "generato dall'engine dello store",
  "wb.file.readonly": "sola lettura",
  "wb.file.unsaved": "modifiche non salvate",
  "wb.file.discard": "Scarta",
  "wb.file.saving": "Salvataggio…",
  "wb.file.save": "Salva come revisione",
  "wb.file.content": "Contenuto di {path}",
} satisfies Dict<typeof en>;

const fr = {
  "wb.breadcrumb": "Fil d'Ariane",
  "wb.draft": "brouillon r{rev}",
  "wb.search.aria": "Rechercher des clés",
  "wb.search.placeholder": "Rechercher clés, libellés, rôles",
  "wb.search.kbd": "Ctrl K",
  "wb.val.none": "Non validé",
  "wb.val.ok.one": "Valide · {count} avertissement",
  "wb.val.ok.many": "Valide · {count} avertissements",
  "wb.val.err.one": "{count} erreur",
  "wb.val.err.many": "{count} erreurs",
  "wb.keys.count": "{done} / {total} clés",
  "wb.theme": "Passer en clair ou en sombre",
  "wb.assistant.toggle": "Afficher ou masquer l'assistant",
  "wb.publish": "Publier",
  "wb.publish.blocked": "{count} élément(s) de la revue bloquent la publication",
  "wb.publish.title": "Versions et publication",
  "wb.act.nav": "Espace de travail",
  "wb.act.explorer": "Explorateur",
  "wb.act.design": "Conception : exigences et protocole",
  "wb.act.review": "Revue",
  "wb.act.validate": "Valider le brouillon",
  "wb.act.examples": "Exemples",
  "wb.act.help": "Aide - manuel d'utilisation",
  "wb.exp.package": "Paquet",
  "wb.exp.add.c": "Ajouter des fichiers au paquet (decode.js, encode.js, exemples)",
  "wb.exp.add.pb": "Ajouter des fichiers au paquet (.proto, exemples)",
  "wb.exp.empty.c": "Rien pour l'instant : cliquez sur « Créer le paquet » dans la configuration pour commencer.",
  "wb.exp.empty.b": "Rien pour l'instant : créez le paquet, puis décrivez l'appareil dans l'onglet Conception.",
  "wb.exp.empty.a": "Aucune source pour l'instant : téléversez le .proto pour commencer.",
  "wb.exp.showfiles": "Afficher les fichiers",
  "wb.exp.fold": "Replier",
  "wb.exp.ai": "IA",
  "wb.exp.inputs": "Documents",
  "wb.exp.add.inputs": "Ajouter des documents (PDF, images, captures)",
  "wb.exp.inputs.empty": "Fiches techniques, documents de protocole et captures que l'IA peut lire.",
  "wb.exp.groups": "Groupes de clés",
  "wb.exp.messages": "Messages",
  "wb.group.commands": "Commandes",
  "wb.group.keys": "Clés",
  "wb.tab.design": "Conception",
  "wb.tab.review": "Revue · {group}",
  "wb.tab.examples": "Exemples",
  "wb.tab.close": "Fermer {file}",
  "wb.filter.aria": "Filtre",
  "wb.filter.all": "Toutes {count}",
  "wb.filter.review": "À revoir {count}",
  "wb.filter.low": "Confiance faible {count}",
  "wb.filter.questions": "Questions {count}",
  "wb.selected": "{count} sélectionnée(s)",
  "wb.reject": "Rejeter",
  "wb.accept.n": "Accepter {count}",
  "wb.adopt.title": "L'assistant a modifié sa proposition pour des clés que vous aviez acceptées : reprendre toutes les nouvelles propositions.",
  "wb.adopt.title.edited": "{count} clé(s) que vous avez modifiée(s) sont exclues - ouvrez-les pour comparer et reprendre la nouvelle proposition une par une.",
  "wb.adopt.busy": "Reprise…",
  "wb.adopt": "Reprendre toutes les nouvelles propositions ({count})",
  "wb.accepthigh": "Accepter toutes celles à confiance élevée ({count})",
  "wb.written": "Écrit dans le paquet",
  "wb.write.title.c": "Écrire les clés acceptées et modifiées dans catalogue.json et valider",
  "wb.write.title.pb": "Écrire les clés acceptées et modifiées dans classification.yaml, régénérer catalogue.json et valider",
  "wb.write.busy": "Écriture…",
  "wb.write": "Écrire {count} dans le paquet",
  "wb.grid.search": "Recherche « {q} »",
  "wb.col.key": "Clé",
  "wb.col.class": "Classe",
  "wb.col.kind": "Type · unité",
  "wb.col.role": "Rôle · qualificatifs",
  "wb.col.label": "Libellé",
  "wb.col.why": "Pourquoi",
  "wb.col.conf": "Confiance",
  "wb.col.state": "État",
  "wb.grid.shown.one": "{count} clé affichée",
  "wb.grid.shown.many": "{count} clés affichées",
  "wb.grid.none": "Rien à afficher avec ce filtre.",
  "wb.grid.select": "Sélectionner {key}",
  "wb.grid.diff.title": "L'IA propose une modification",
  "wb.state.accepted": "acceptée",
  "wb.state.proposed": "proposée",
  "wb.state.edited": "modifiée",
  "wb.state.question": "question",
  "wb.state.rejected": "rejetée",
  "wb.state.open": "ouverte",
  "wb.state.answered": "répondue",
  "wb.state.dismissed": "écartée",
  "wb.conf.high": "élevée",
  "wb.conf.medium": "moyenne",
  "wb.conf.low": "faible",
  "wb.rev.none": "Aucune révision pour l'instant.",
  "wb.rev.ai": "IA",
  "wb.rev.person": "personne",
  "wb.geo.positions": "{count} position(s)",
  "wb.geo.wifi": "{count} Wi-Fi",
  "wb.geo.cells": "{count} cellule(s)",
  "wb.geo.ble": "{count} BLE",
  "wb.geo.note": " · géo : {list}",
  "wb.smp.nopkg": "Pas encore de paquet.",
  "wb.smp.running": "Exécution des exemples…",
  "wb.smp.summary": "{ok} exemples sur {total} se décodent comme prévu (le store en demande au moins 5).",
  "wb.smp.open": "Ouvrir l'onglet Exemples",
  "wb.smp.ok": "se décode comme prévu · {ms} ms{geo}",
  "wb.smp.differs": "diffère",
  "wb.panel.aria": "Panneau",
  "wb.panel.problems": "Problèmes",
  "wb.panel.samples": "Exemples",
  "wb.panel.revisions": "Révisions",
  "wb.panel.engine": "glmdt check · moteur {engine} · {ms} ms · r{rev}",
  "wb.panel.checking": "Vérification…",
  "wb.panel.validate": "Valider",
  "wb.panel.notvalidated": "Pas encore validé.",
  "wb.ai.aria": "Assistant IA",
  "wb.ai.title": "Assistant",
  "wb.ai.scenario": "Scénario {s} · {kind}",
  "wb.ai.paused": "En pause",
  "wb.ai.working": "Au travail",
  "wb.ai.ready": "Prêt",
  "wb.ai.hide": "Masquer l'assistant",
  "wb.ai.pausedtext": "L'IA est en pause : la passerelle de la console est injoignable. Le projet reste modifiable ; la conversation reprendra à son retour.",
  "wb.ai.empty.nofiles": "Créez d'abord le paquet (configuration, étape 1) : l'assistant a besoin du manifest pour vérifier son travail. Téléversez ensuite les documents et demandez-lui de commencer.",
  "wb.ai.empty.c": "Demandez à l'assistant d'écrire le décodeur. Il lit vos documents et exemples, écrit decode.js, propose chaque clé émise dans la grille de revue et exécute vos exemples jusqu'à ce qu'ils passent ; il pose des questions quand il n'est pas sûr. Rien n'entre dans le paquet tant que vous ne l'acceptez pas.",
  "wb.ai.empty.b": "Décrivez l'appareil dans l'onglet Conception, puis demandez à l'assistant de rédiger les exigences. Après votre confirmation, il conçoit le protocole et le classifie. Rien n'entre dans le paquet tant que vous ne l'acceptez pas.",
  "wb.ai.empty.a": "Demandez à l'assistant de classifier le .proto. Il lit le schéma et vos documents, propose chaque clé dans la grille de revue et pose des questions quand il n'est pas sûr. Rien n'entre dans le paquet tant que vous ne l'acceptez pas.",
  "wb.ai.thinking": "Réflexion…",
  "wb.ai.question": "Question {n} sur {total}",
  "wb.ai.question.blocking": "Question {n} sur {total} · bloque la publication",
  "wb.ai.answer.label": "Votre réponse",
  "wb.ai.answer": "Répondre",
  "wb.ai.dismiss": "Écarter",
  "wb.ai.ask": "Demander à l'assistant",
  "wb.ai.ph.questions": "Questions répondues ? Dites à l'assistant de continuer",
  "wb.ai.ph.c": "p. ex. Écris le décodeur à partir des documents téléversés et de mes exemples",
  "wb.ai.ph.a": "Demandez une modification, p. ex. utiliser le rôle signal_strength pour chaque clé *_rssi_dbm",
  "wb.ai.busy": "L'assistant travaille…",
  "wb.ai.hint": "Entrée pour envoyer · Maj+Entrée nouvelle ligne",
  "wb.ai.send": "Envoyer",
  "wb.st.draft": "brouillon · r{rev}",
  "wb.st.engine": "Moteur {engine}",
  "wb.st.quickjs": "Bac à sable QuickJS",
  "wb.st.pb": "décodeur protobuf",
  "wb.st.demo": "Données de démo",
  "wb.st.region": "Région {region}",
  "wb.st.usage": "Utilisation IA aujourd'hui {usage}",
  "wb.file.loading": "Chargement de {path}…",
  "wb.file.binary": "{path} est un fichier binaire (une charge utile d'exemple) : il est décodé dans le panneau Exemples.",
  "wb.file.generated": "généré par le moteur du store",
  "wb.file.readonly": "lecture seule",
  "wb.file.unsaved": "modifications non enregistrées",
  "wb.file.discard": "Abandonner",
  "wb.file.saving": "Enregistrement…",
  "wb.file.save": "Enregistrer comme révision",
  "wb.file.content": "Contenu de {path}",
} satisfies Dict<typeof en>;

export default { en, de, it, fr };
